/**
 * Shared view utilities for PlanView and ProfileView.
 */

/**
 * Toggle fullscreen on a DOM element by ID.
 * @param {string} containerId - DOM id of the element to fullscreen
 * @param {Function} [onEnter] - Optional callback invoked after entering fullscreen
 */
export function toggleFullscreen(containerId, onEnter) {
  const container = document.getElementById(containerId)
  if (!container) {
    console.error(`toggleFullscreen: element #${containerId} not found, fullscreen ignored`)
    return
  }

  if (document.fullscreenElement) {
    document.exitFullscreen()
  } else {
    container.requestFullscreen().then(() => {
      if (onEnter) onEnter()
    }).catch(err => {
      console.warn('Fullscreen error:', err)
    })
  }
}

/**
 * Show a temporary toast notification at the top center of the screen.
 * @param {string} message - Text to display
 * @param {number} [duration=3000] - Duration in ms before auto-removal
 */
export function showToast(message, duration = 3000) {
  const toast = document.createElement('div')
  toast.className = 'toast toast-center toast-top z-50'

  const alert = document.createElement('div')
  alert.className = 'alert alert-info'

  // textContent, not innerHTML: the message can carry user data (plan name,
  // dive site) which must never be parsed as markup
  const text = document.createElement('span')
  text.textContent = message
  alert.appendChild(text)
  toast.appendChild(alert)

  document.body.appendChild(toast)
  setTimeout(() => toast.remove(), duration)
}

/**
 * Escape a string for safe interpolation into an HTML template literal.
 *
 * Views build their markup with template literals, so any value coming from
 * the diver has to be escaped on the way in. A plan name is the obvious case:
 * it is typed by the diver, and it also travels through the JSON import, which
 * accepts files this application never wrote.
 *
 * @param {*} value - Value to escape; non-strings are stringified
 * @returns {string} HTML-safe text
 */
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Number typed in a bounded numeric field, or null when the field holds
 * nothing usable - in which case the reason is logged and the field is put
 * back to `fallback`, the value the store still holds.
 *
 * The bounds are the min / max attributes of the input itself, so a rule
 * written once in the markup is the rule enforced. This exists because every
 * settings handler wrote parseFloat(e.target.value) straight into the store:
 * an emptied field parses to NaN, NaN was persisted, and every calculation
 * failed with a generic message until the diver found the field again.
 * Number(''), which is 0, is refused explicitly: an emptied rate is not a rate
 * of zero.
 *
 * @param {HTMLInputElement|null} input - The field, with optional min / max
 * @param {Object} [options]
 * @param {number} [options.fallback] - Value written back into the field on refusal
 * @param {boolean} [options.integer] - Refuse a fractional value
 * @param {string} [options.context] - Name of the caller, for the log
 * @returns {number|null}
 */
export function readBoundedNumber(input, { fallback, integer = false, context = 'view' } = {}) {
  if (!input) {
    console.error('%s: numeric field missing from the page, nothing read', context)
    return null
  }
  const bound = (attr, fallbackBound) => {
    const raw = input[attr]
    if (raw === undefined || raw === null || raw === '') return fallbackBound
    const value = Number(raw)
    return Number.isFinite(value) ? value : fallbackBound
  }
  const min = bound('min', -Infinity)
  const max = bound('max', Infinity)
  const raw = input.value
  const value = raw === '' || raw === null || raw === undefined ? Number.NaN : Number(raw)
  const usable = Number.isFinite(value) && value >= min && value <= max && (!integer || Number.isInteger(value))
  if (!usable) {
    console.error('%s: rejected %s = %o (allowed %o to %o%s), keeping %o',
      context, input.id || 'field', raw, min, max, integer ? ', whole numbers' : '', fallback)
    if (fallback !== undefined && fallback !== null) input.value = fallback
    return null
  }
  return value
}
